import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, Globe, Instagram, LogOut, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { APPT_STATUS, APPT_TONE, COMPANY, apptTypeLabel, fmtDateTime } from "@/lib/format";
import { Btn, Field, PageHeader, Pill } from "@/components/rp";
import { BookingForm } from "@/components/BookingForm";

const Section = ({ label, children, id, className = "" }) => (
  <section id={id} className={`rp-card p-6 sm:p-8 ${className}`}>
    <p className="rp-label mb-5">{label}</p>
    {children}
  </section>
);

const Upcoming = ({ items, onCancel }) => (
  <div className="space-y-2" data-testid="upcoming-appointments">
    {!items.length && <p className="text-sm text-[#757575]">Nessun appuntamento in programma.</p>}
    {items.map((a) => (
      <div key={a.id} className="flex items-center gap-4 bg-white rounded-2xl border border-[#EBEBEB] p-4" data-testid={`appointment-${a.id}`}>
        <CalendarDays className="h-5 w-5 text-[#C8BDAB] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{a.title}</p>
          <p className="text-xs text-[#757575]">{fmtDateTime(a.date)} · {apptTypeLabel(a.type)} · {a.location}</p>
        </div>
        <Pill tone={APPT_TONE[a.status]}>{APPT_STATUS[a.status]}</Pill>
        <button onClick={() => onCancel(a)} className="text-sm text-[#DC3545] hover:underline shrink-0" data-testid={`appointment-cancel-${a.id}`}>Annulla</button>
      </div>
    ))}
  </div>
);

const PasswordForm = () => {
  const [f, setF] = useState({ old_password: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    setLoading(true);
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password aggiornata");
      setF({ old_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <Field label="Password attuale"><input type="password" required value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} className="rp-field" data-testid="old-password-input" /></Field>
      <Field label="Nuova password" hint="Almeno 8 caratteri"><input type="password" required value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="rp-field" data-testid="new-password-input" /></Field>
      <Btn type="submit" variant="secondary" loading={loading} data-testid="change-password-submit">Aggiorna password</Btn>
    </form>
  );
};

export default function ClientProfilo() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [appts, setAppts] = useState([]);
  const load = useCallback(() => api.get("/appointments").then((r) => setAppts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (hash === "#prenota") setTimeout(() => document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [hash]);

  const upcoming = appts.filter((a) => ["richiesto", "confermato"].includes(a.status) && new Date(a.date) >= new Date(Date.now() - 3600e3));
  const cancel = async (a) => {
    try {
      await api.patch(`/appointments/${a.id}`, { status: "annullato" });
      toast.success("Appuntamento annullato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader label="Profilo" title={user.name} />
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <Section label="I tuoi dati">
            <dl className="space-y-3 text-[15px]" data-testid="profile-data">
              {[["Email", user.email], ["Telefono", user.phone || "—"], ["Indirizzo", user.address || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[#EBEBEB] pb-3 last:border-0"><dt className="text-[#757575]">{k}</dt><dd className="text-right">{v}</dd></div>
              ))}
            </dl>
          </Section>
          <Section label="Prossimi appuntamenti"><Upcoming items={upcoming} onCancel={cancel} /></Section>
          <Link to="/richiesta-preventivo" data-testid="profile-new-quote-banner" className="group block rounded-[20px] bg-[#070707] text-white p-8 overflow-hidden relative">
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/60 font-semibold">Un nuovo progetto?</p>
            <p className="text-2xl font-semibold tracking-tight mt-2 flex items-center gap-3">Richiedi un nuovo preventivo <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></p>
            <div className="absolute -right-10 -bottom-16 h-40 w-40 rounded-full bg-[#C8BDAB]/30" />
          </Link>
          <Section label="Contatti">
            <div className="space-y-3 text-[15px]" data-testid="profile-contacts">
              <a href={COMPANY.maps} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline" data-testid="contact-maps-link"><MapPin className="h-4 w-4" /> {COMPANY.address}</a>
              <a href={COMPANY.site} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline" data-testid="contact-site-link"><Globe className="h-4 w-4" /> www.rphome.it</a>
              <a href={COMPANY.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline" data-testid="contact-instagram-link"><Instagram className="h-4 w-4" /> @rphome.it</a>
            </div>
          </Section>
        </div>
        <div className="space-y-5">
          <Section label="Prenota un appuntamento" id="prenota"><BookingForm onBooked={load} /></Section>
          <Section label="Cambia password"><PasswordForm /></Section>
          <Btn variant="outline" className="w-full" onClick={() => { logout(); navigate("/login"); }} data-testid="profile-logout-button"><LogOut className="h-4 w-4" /> Esci</Btn>
        </div>
      </div>
    </div>
  );
}
